import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { PaperPlaneIcon, TextToVoiceIcon } from '@rsos/assets/icons';
import { Absolute, Box, Loader, Text } from '@rsos/base-components';
import { InputStyles } from '@rsos/base-components/form/inputs/InputText/InputText.styles';
import Popover from '@rsos/components/capstone/base/Popover';
import { useFlag } from '@rsos/flags';
import { trackTextToVoiceInvoked } from '../../../../../../gainsightTrackings/textToVoiceTrackings';
import useHasAccessToCommunicatorTextToVoiceTranslation from '../../../../../../hooks/useHasAccessToCommunicatorTextToVoiceTranslation';
import useHasAccessToManualLanguageSelection from '../../../../../../hooks/useHasAccessToManualLanguageSelection';
import {
  selectTranscript,
  maximize,
  minimize,
  selectTranscriptionForCall,
} from '../transcriptionSlice';
import CopyTranscript from './CopyTranscript';
import LanguageChangeDivider from './LanguageChangeDivider';
import LanguagePillSelector from './LanguageSelector/LanguagePillSelector';
import LanguagePillSelectorV2 from './LanguageSelector/LanguagePillSelectorV2';
import ManualLanguageSelector from './ManualLanguageSelector';
import MaximizeToggle from './MaximizeToggle';
import NewMessagesIndicator from './NewMessagesIndicator';
import TextToVoiceModal from './TextToVoiceModal/TextToVoiceModal';
import TextToVoiceToggle from './TextToVoiceToggle';
import TranscriptKeywordFilter from './TranscriptKeywordFilter';
import TranscriptTokenWithHighlighting from './TranscriptTokenWithHighlighting';
import {
  StyledTranscriptViewer,
  StyledTranscriptTitle,
  StyledTranscriptHeader,
  StyledSpeaker,
  StyledTimestamp,
  StyledMessage,
  StyledToken,
  StyledLoaderContainer,
  StyledTranscriptTokens,
  ButtonContainer,
  StyledHoverableText,
  PopoverBox,
  PopoverText,
  LanguageControlsWrapper,
} from './TranscriptViewer.styles';
import { TEXT_TO_VOICE_LANGUAGES_SET } from './constants';
import mergeTokens from './mergeTokens';
import {
  activateInput,
  clearSession,
  setSessionData,
} from './textToVoiceSlice';
import {
  formatPhoneNumberSlice,
  callMidasTrigger,
} from './transcriptViewerUtils';

const TEXTAREA_MAX_HEIGHT = 104;
const DEFAULT_TTVT_TIMEOUTS = {
  timeout_minutes: 30,
};
const DEFAULT_TTVT_TTS_MODEL = 'eleven_multilingual_v2';

const resolveTtvtTimeoutMinutes = value => {
  if (value && typeof value === 'object') {
    const withMinutes = value.timeout_minutes;
    if (
      typeof withMinutes === 'number' &&
      Number.isFinite(withMinutes) &&
      withMinutes > 0
    ) {
      return withMinutes;
    }
  }

  return DEFAULT_TTVT_TIMEOUTS.timeout_minutes;
};

const resolveTtvtTtsModel = value => {
  if (typeof value === 'string' && value.trim()) {
    return value.trim();
  }

  if (value && typeof value === 'object') {
    const fromObject = value.tts_model ?? value.model;
    if (typeof fromObject === 'string' && fromObject.trim()) {
      return fromObject.trim();
    }
  }

  return DEFAULT_TTVT_TTS_MODEL;
};

export const formatTimestamp = timestamp => {
  try {
    const date = new Date(timestamp);
    return new Intl.DateTimeFormat('default', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).format(date);
  } catch {
    return '';
  }
};

const Container = styled(Box).attrs({
  position: 'relative',
  height: '100%',
  overflowX: 'hidden',
  overflowY: 'auto',
  minHeight: '50px',
})``;

const TextToVoiceContainer = styled(Box).attrs({
  bg: 'foreground',
  border: '1px solid',
  borderColor: 'heavyLineOutline',
  width: '100%',
  borderRadius: '0px 0px 5px 5px',
})`
  border-top-width: 0;
`;

const TextArea = styled.textarea`
  ${InputStyles}
  resize: none;
  overflow: auto;
  max-height: ${TEXTAREA_MAX_HEIGHT}px;
  height: auto;
  // &::placeholder {
  //   padding: 8px;
  // }
  padding: 8px;
`;

const TranscriptToken = ({ token, selectedTranscript }) => {
  let transcript = token?.transcript;
  if (selectedTranscript === 'translated' && token?.translated) {
    transcript = token?.translated;
  }

  return (
    <StyledMessage>
      <StyledSpeaker
        speakerName={token?.speaker_name}
        data-name="transcript-token-speaker"
      >
        {token?.speaker_display_name}
      </StyledSpeaker>{' '}
      {token?.start_time && (
        <StyledTimestamp>{formatTimestamp(token?.start_time)}</StyledTimestamp>
      )}
      <StyledToken data-name="transcript-token-message">
        "{transcript}"
      </StyledToken>
    </StyledMessage>
  );
};

const TranscriptViewer = ({
  callerId,
  isTranscriptMaximized = false,
  transcriptionOverride = null,
  readOnly = false,
}) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [textToVoiceInputValue, setTextToVoiceInputValue] = useState('');
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [newMessagesWhileScrolledUp, setNewMessagesWhileScrolledUp] =
    useState(false);
  const [isPinTooltipVisible, setIsPinTooltipVisible] = useState(false);
  const [isPhoneTooltipVisible, setIsPhoneTooltipVisible] = useState(false);
  const [isPhoneNumberCopied, setIsPhoneNumberCopied] = useState(false);
  const [isPinCopied, setIsPinCopied] = useState(false);

  const languageDetectionHandled = useRef(false);
  const viewerRef = useRef(null);
  const pinPopoverRef = useRef();
  const phonePopoverRef = useRef();

  const dispatch = useDispatch();
  const selectedTranscript = useSelector(
    state => state.transcriptions.selectedTranscript,
  );
  const stateTranscription = useSelector(selectTranscriptionForCall(callerId));
  const originalTranscription = transcriptionOverride || stateTranscription;
  const transcription = mergeTokens(originalTranscription);
  const languageChangeEvents =
    originalTranscription?.languageChangeEvents || [];
  const manualLanguage = originalTranscription?.manualLanguage;
  const stateHasSummary = useSelector(state => {
    const summaries = selectTranscriptionForCall(callerId)(state)?.summaries;
    return summaries && summaries.length > 0;
  });
  const isReadOnlyView = readOnly || !!transcriptionOverride;
  const hasSummary = isReadOnlyView
    ? (originalTranscription?.summaries || []).length > 0
    : stateHasSummary;
  const isTextToVoiceSelected = useSelector(
    state => state?.textToVoice?.calls?.[callerId]?.visible,
  );
  const pinJobID = useSelector(
    state => state?.textToVoice?.calls?.[callerId]?.pinJobID ?? null,
  );
  const midasConferenceNumber = useSelector(
    state =>
      state?.textToVoice?.calls?.[callerId]?.midasConferenceNumber ?? null,
  );
  const sessionStartedAt = useSelector(
    state => state?.textToVoice?.calls?.[callerId]?.sessionStartedAt ?? null,
  );
  const storedCallSessionKey = useSelector(
    state => state?.textToVoice?.calls?.[callerId]?.callSessionKey ?? null,
  );

  const hasAccessToTextToVoiceTranslation =
    useHasAccessToCommunicatorTextToVoiceTranslation();

  const hasAccessToManualLanguageSelection =
    useHasAccessToManualLanguageSelection();
  const ttvtTimeoutsFlag = useFlag('ttvt_timeouts');
  const ttvtTtsModelFlag = useFlag('ttvt_tts_model');
  const ttvtTimeoutMinutes = useMemo(
    () => resolveTtvtTimeoutMinutes(ttvtTimeoutsFlag),
    [ttvtTimeoutsFlag],
  );
  const ttvtTtsModel = useMemo(
    () => resolveTtvtTtsModel(ttvtTtsModelFlag),
    [ttvtTtsModelFlag],
  );
  const textToVoiceSessionTtlMs = ttvtTimeoutMinutes * 60 * 1000;

  const handleScroll = () => {
    if (viewerRef.current) {
      const BORDER_WIDTHS = 2;
      const { scrollTop, scrollHeight, clientHeight } = viewerRef.current;
      const atBottom = scrollTop + clientHeight >= scrollHeight - BORDER_WIDTHS;
      setIsAtBottom(atBottom);
      if (atBottom) {
        setNewMessagesWhileScrolledUp(false);
      }
    }
  };

  // Reattach listener whenever the transcription changes
  useEffect(() => {
    const attachScrollListener = () => {
      const viewer = viewerRef.current;
      if (viewer) {
        viewer.addEventListener('scroll', handleScroll);
      }
    };

    // Attach listener when the component mounts or when transcription changes
    attachScrollListener();

    return () => {
      const viewer = viewerRef.current;
      if (viewer) {
        viewer.removeEventListener('scroll', handleScroll);
      }
    };
  }, [transcription]);

  // When changing between calls, resets state of notification for new messages, the status for the scroll, flag isAtBottom and language buttons
  useEffect(() => {
    dispatch(selectTranscript('original'));
    setNewMessagesWhileScrolledUp(false);
    if (viewerRef.current) {
      viewerRef.current.scrollTop = viewerRef.current.scrollHeight;
    }
    setIsAtBottom(true);
  }, [callerId, dispatch]);

  // When transcription changes auto-scroll to the newest message.
  // NumTokens has been created to avoid a sporatic (and difficult to reproduce) bug
  // where transcription is sometimes undefined/null which causes a crash.
  const numTokens = transcription?.payload?.tokens?.length ?? 0;
  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;
    if (isAtBottom && numTokens > 0) {
      // autoscroll only if user is already at the bottom
      viewerRef.current.scrollTop = viewerRef.current.scrollHeight;
    } else if (!isAtBottom && numTokens > 0) {
      // if new messages arrive while not at the bottom, set the indicator state
      setNewMessagesWhileScrolledUp(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numTokens]);

  useEffect(() => {
    if (
      transcription?.payload?.languagesDetected?.length > 0 &&
      !languageDetectionHandled.current
    ) {
      dispatch(selectTranscript('translated'));
      languageDetectionHandled.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transcription]);

  const { tokens = [] } = transcription?.payload || {};
  const detectedLanguages = transcription?.payload?.languagesDetected || [];
  const hasDetectedLanguages = detectedLanguages.length > 0;
  const currentCallInfo = transcription?.payload?.call_information;
  const currentCallSessionKey =
    currentCallInfo?.digi_call_id || currentCallInfo?.call_start_time || null;
  const audioStatus = transcription?.payload?.audio_data?.status;
  const isCallEnded =
    audioStatus === 'complete' || audioStatus === 'unavailable';
  const hasPersistedSession = Boolean(pinJobID && midasConferenceNumber);
  const isSessionExpired = sessionStartedAt
    ? Date.now() - sessionStartedAt >= textToVoiceSessionTtlMs
    : false;
  const doesSessionMatchCurrentCall =
    !storedCallSessionKey ||
    !currentCallSessionKey ||
    storedCallSessionKey === currentCallSessionKey;
  const isManualLanguageEnabled = hasAccessToManualLanguageSelection;
  const hasManualLanguageChanges =
    isManualLanguageEnabled && languageChangeEvents.length > 0;
  const shouldShowLanguagePills =
    hasDetectedLanguages || hasManualLanguageChanges;

  const selectedLanguageCodeForTextToVoice = useMemo(() => {
    if (hasAccessToManualLanguageSelection) {
      const lastDetectedLanguageFromTokens =
        tokens?.[tokens.length - 1]?.detected_language ?? null;
      const lastDetectedLanguageFromList =
        detectedLanguages?.[detectedLanguages.length - 1] ?? null;

      return (
        manualLanguage ||
        lastDetectedLanguageFromTokens ||
        lastDetectedLanguageFromList ||
        null
      );
    }

    return detectedLanguages?.[0] ?? null;
  }, [
    detectedLanguages,
    hasAccessToManualLanguageSelection,
    manualLanguage,
    tokens,
  ]);

  // If the call ends while TTVT is active, clear persisted session data.
  useEffect(() => {
    if (isCallEnded && (isTextToVoiceSelected || hasPersistedSession)) {
      dispatch(clearSession(callerId));
      setIsModalVisible(false);
    }
  }, [
    callerId,
    dispatch,
    hasPersistedSession,
    isCallEnded,
    isTextToVoiceSelected,
  ]);

  // Once we have call metadata, bind an existing session to the current call key.
  useEffect(() => {
    if (hasPersistedSession && !storedCallSessionKey && currentCallSessionKey) {
      dispatch(
        setSessionData({
          callerId,
          callSessionKey: currentCallSessionKey,
        }),
      );
    }
  }, [
    callerId,
    currentCallSessionKey,
    dispatch,
    hasPersistedSession,
    storedCallSessionKey,
  ]);

  // If the call identity changes for the same caller, clear stale TTVT session data.
  useEffect(() => {
    if (
      hasPersistedSession &&
      storedCallSessionKey &&
      currentCallSessionKey &&
      storedCallSessionKey !== currentCallSessionKey
    ) {
      dispatch(clearSession(callerId));
      setIsModalVisible(false);
    }
  }, [
    callerId,
    currentCallSessionKey,
    dispatch,
    hasPersistedSession,
    storedCallSessionKey,
  ]);

  // Auto-expire persisted TTVT session data after 1 hour.
  useEffect(() => {
    if (!hasPersistedSession || !sessionStartedAt) {
      return undefined;
    }

    const elapsed = Date.now() - sessionStartedAt;
    const remaining = textToVoiceSessionTtlMs - elapsed;

    if (remaining <= 0) {
      dispatch(clearSession(callerId));
      setIsModalVisible(false);
      return undefined;
    }

    const timer = setTimeout(() => {
      dispatch(clearSession(callerId));
      setIsModalVisible(false);
    }, remaining);

    return () => clearTimeout(timer);
  }, [
    callerId,
    dispatch,
    hasPersistedSession,
    sessionStartedAt,
    textToVoiceSessionTtlMs,
  ]);

  const effectiveLanguages = useMemo(() => {
    const languages = new Set(detectedLanguages);

    if (isManualLanguageEnabled && languageChangeEvents.length > 0) {
      languageChangeEvents.forEach(event => {
        if (event.language && event.language !== 'en') {
          languages.add(event.language);
        }
      });
    }

    return Array.from(languages);
  }, [detectedLanguages, isManualLanguageEnabled, languageChangeEvents]);

  const tokensWithDividers = useMemo(() => {
    if (!isManualLanguageEnabled || !languageChangeEvents.length) {
      return tokens.map(token => ({ type: 'token', data: token }));
    }

    const combined = [
      ...tokens.map(token => ({
        type: 'token',
        data: token,
        sortTime: token.start_time,
        sequenceNumber: Infinity, // Tokens don't have sequence numbers, so they sort after events
      })),
      ...languageChangeEvents.map(event => ({
        type: 'languageChange',
        data: event,
        sortTime: event.timestamp,
        sequenceNumber: event.sequenceNumber ?? 0,
      })),
    ];

    // Sort by timestamp first, then by sequence number to maintain insertion order
    combined.sort((a, b) => {
      if (a.sortTime !== b.sortTime) {
        return a.sortTime - b.sortTime;
      }
      return (a.sequenceNumber ?? 0) - (b.sequenceNumber ?? 0);
    });

    return combined;
  }, [tokens, languageChangeEvents, isManualLanguageEnabled]);

  const handlePinHover = isVisible => setIsPinTooltipVisible(isVisible);
  const handlePinMouseEnter = () => handlePinHover(true);
  const handlePinMouseLeave = () => handlePinHover(false);

  const handlePhoneHover = isVisible => setIsPhoneTooltipVisible(isVisible);
  const handlePhoneMouseEnter = () => handlePhoneHover(true);
  const handlePhoneMouseLeave = () => handlePhoneHover(false);

  const handlePinCopy = () => {
    if (!navigator.clipboard) {
      console.error(
        'Copy feature unavailable. It is only available on a secure (HTTPS) connection.',
      );
      return;
    }
    navigator.clipboard.writeText(pinJobID || '');
    setIsPinCopied(true);
    setTimeout(() => {
      setIsPinCopied(false);
    }, 3000);
  };

  const handlePhoneNumberCopy = () => {
    if (!navigator.clipboard) {
      console.error(
        'Copy feature unavailable. It is only available on a secure (HTTPS) connection.',
      );
      return;
    }
    if (!midasConferenceNumber) return;
    const massagedNumber = midasConferenceNumber.replace(/\D/g, '');
    navigator.clipboard.writeText(massagedNumber);
    setIsPhoneNumberCopied(true);
    setTimeout(() => {
      setIsPhoneNumberCopied(false);
    }, 3000);
  };

  const handleMaximize = () => {
    if (isTranscriptMaximized) {
      dispatch(minimize());
    } else {
      dispatch(maximize());
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();

    try {
      await handleSendMessage();
    } catch (err) {
      console.log(err);
    }
  };

  const callMidas = async payload => {
    const data = await callMidasTrigger(payload);

    if (!data) {
      // If we were trying to use an existing session (job_id), treat this as
      // stale session data and clear persisted values.
      if (payload?.variables?.job_id) {
        dispatch(clearSession(callerId));
      }
      return;
    }

    const nextPinJobID = data.job_id || null;
    const nextConferenceNumber = data.communicator_translator_phone
      ? formatPhoneNumberSlice(data.communicator_translator_phone)
      : null;

    if (nextPinJobID || nextConferenceNumber) {
      dispatch(
        setSessionData({
          callerId,
          pinJobID: nextPinJobID,
          midasConferenceNumber: nextConferenceNumber,
          callSessionKey: currentCallSessionKey,
        }),
      );
    }
  };

  const handleMidasPhoneNumber = async () => {
    trackTextToVoiceInvoked({
      detectedLanguages,
      selectedLanguage: selectedLanguageCodeForTextToVoice,
    });

    if (isCallEnded) {
      return;
    }

    if (hasPersistedSession && isSessionExpired) {
      dispatch(clearSession(callerId));
      setIsModalVisible(false);
    }

    if (hasPersistedSession && !doesSessionMatchCurrentCall) {
      dispatch(clearSession(callerId));
      setIsModalVisible(false);
    }

    if (
      !isSessionExpired &&
      doesSessionMatchCurrentCall &&
      midasConferenceNumber &&
      pinJobID
    ) {
      setIsModalVisible(true);
      dispatch(activateInput(callerId));
      return;
    }

    if (!selectedLanguageCodeForTextToVoice) {
      return;
    }

    const payload = {
      callflow: 'communicator-translation',
      blocking: true,
      variables: {
        target_language: selectedLanguageCodeForTextToVoice,
        pin_timeout: textToVoiceSessionTtlMs,
        session_timeout: textToVoiceSessionTtlMs,
        tts_model: ttvtTtsModel,
      },
    };

    await callMidas(payload);
    setIsModalVisible(true);
    dispatch(activateInput(callerId));
  };

  async function handleSendMessage() {
    if (!textToVoiceInputValue) {
      return;
    }

    if (isCallEnded) {
      return;
    }

    if (isSessionExpired || !doesSessionMatchCurrentCall) {
      dispatch(clearSession(callerId));
      return;
    }

    if (!pinJobID) {
      return;
    }

    const targetLanguageCode = selectedLanguageCodeForTextToVoice;

    if (!targetLanguageCode) {
      return;
    }

    if (targetLanguageCode.split('-')[0].toLowerCase() === 'en') {
      return;
    }

    const payload = {
      callflow: 'communicator-translation',
      blocking: true,
      variables: {
        job_id: pinJobID,
        message: textToVoiceInputValue,
        target_language: targetLanguageCode,
      },
    };

    setTextToVoiceInputValue('');

    await callMidas(payload);
  }

  const isKeywordSetsEnabled = useFlag('keyword_sets');

  const supportsLanguages = useMemo(() => {
    // Keep the new gating logic behind manual language selection access.
    // When enabled, only require that the *current* language (manual selection
    // or last detected) is supported by TTVT.
    if (hasAccessToManualLanguageSelection) {
      const lastDetectedLanguageFromTokens =
        tokens?.[tokens.length - 1]?.detected_language ?? null;
      const lastDetectedLanguageFromList =
        detectedLanguages?.[detectedLanguages.length - 1] ?? null;
      const languageToCheck =
        manualLanguage ||
        lastDetectedLanguageFromTokens ||
        lastDetectedLanguageFromList;

      return (
        !!languageToCheck && TEXT_TO_VOICE_LANGUAGES_SET.has(languageToCheck)
      );
    }

    // Legacy gating: require that all detected languages are supported.
    return (
      detectedLanguages.length > 0 &&
      detectedLanguages.every(language =>
        TEXT_TO_VOICE_LANGUAGES_SET.has(language),
      )
    );
  }, [
    detectedLanguages,
    hasAccessToManualLanguageSelection,
    manualLanguage,
    tokens,
  ]);

  const hasLanguageForTextToVoice = useMemo(() => {
    if (hasAccessToManualLanguageSelection) {
      const lastDetectedLanguageFromTokens =
        tokens?.[tokens.length - 1]?.detected_language ?? null;
      const lastDetectedLanguageFromList =
        detectedLanguages?.[detectedLanguages.length - 1] ?? null;

      return Boolean(
        manualLanguage ||
          lastDetectedLanguageFromTokens ||
          lastDetectedLanguageFromList,
      );
    }

    return detectedLanguages.length > 0;
  }, [
    detectedLanguages,
    hasAccessToManualLanguageSelection,
    manualLanguage,
    tokens,
  ]);

  let textToVoiceDisabledTooltip = '';
  if (isReadOnlyView) {
    textToVoiceDisabledTooltip =
      'Text to Voice is unavailable in read-only view.';
  } else if (!hasAccessToTextToVoiceTranslation) {
    textToVoiceDisabledTooltip = 'You do not have access to Text to Voice.';
  } else if (!tokens.length) {
    textToVoiceDisabledTooltip =
      'Text to Voice is available after transcript starts.';
  } else if (!hasLanguageForTextToVoice) {
    textToVoiceDisabledTooltip =
      'Text to Voice is unavailable until language is detected.';
  } else if (!supportsLanguages) {
    textToVoiceDisabledTooltip =
      'Text to Voice is unavailable for the detected language.';
  } else if (isCallEnded) {
    textToVoiceDisabledTooltip = 'Text to Voice is unavailable after call end.';
  }
  const isTextToVoiceDisabled = Boolean(textToVoiceDisabledTooltip);
  const textToVoiceTooltip = isTextToVoiceDisabled
    ? textToVoiceDisabledTooltip
    : 'Text to Voice';

  let maximizeTooltip = isTranscriptMaximized
    ? 'Minimize transcript'
    : 'Expand transcript';
  if (!tokens.length) {
    maximizeTooltip = 'Resize is available after transcript starts.';
  } else if (!hasSummary) {
    maximizeTooltip = 'Resize is unavailable until incident summary is ready.';
  }
  const isMaximizeDisabled = !tokens.length || !hasSummary;

  return (
    <>
      <Container>
        <StyledTranscriptViewer
          data-name="transcript-viewer"
          isTextToVoiceSelected={isTextToVoiceSelected}
        >
          <StyledTranscriptHeader
            pb={tokens.length > 0 && !shouldShowLanguagePills ? '12px' : '4px'}
          >
            <Box>
              <StyledTranscriptTitle noMargin>
                Call Audio Transcript
              </StyledTranscriptTitle>
            </Box>
            <ButtonContainer>
              <TextToVoiceToggle
                disabled={isTextToVoiceDisabled}
                tooltip={textToVoiceTooltip}
                isSelected={isTextToVoiceSelected}
                onClick={handleMidasPhoneNumber}
              />
              <CopyTranscript
                selectedTranscript={selectedTranscript}
                transcription={transcription}
              />
              <MaximizeToggle
                disabled={isMaximizeDisabled}
                tooltip={maximizeTooltip}
                isMaximized={isTranscriptMaximized}
                onClick={handleMaximize}
              />
            </ButtonContainer>
          </StyledTranscriptHeader>

          {!isReadOnlyView && isManualLanguageEnabled ? (
            <LanguageControlsWrapper>
              <ManualLanguageSelector callerId={callerId} />
              {shouldShowLanguagePills ? (
                <LanguagePillSelectorV2
                  selectedLanguage={selectedTranscript}
                  detectedLanguages={effectiveLanguages}
                  onChange={transcriptVersion => {
                    dispatch(selectTranscript(transcriptVersion));
                  }}
                />
              ) : (
                <div style={{ flex: 1 }} />
              )}
            </LanguageControlsWrapper>
          ) : (
            shouldShowLanguagePills && (
              <LanguagePillSelector
                selectedLanguage={selectedTranscript}
                detectedLanguages={effectiveLanguages}
                onChange={transcriptVersion => {
                  dispatch(selectTranscript(transcriptVersion));
                }}
              />
            )
          )}

          {isKeywordSetsEnabled && !isReadOnlyView && (
            <TranscriptKeywordFilter />
          )}
          {transcription?.fetching && !tokens.length ? (
            <StyledLoaderContainer>
              <Loader
                fluid
                text="Connecting to transcription service"
                aria-label="Loading transcript"
              />
            </StyledLoaderContainer>
          ) : !tokens.length ? (
            <StyledLoaderContainer>
              <Loader
                fluid
                text="Listening for transcript"
                aria-label="Loading transcript"
              />
            </StyledLoaderContainer>
          ) : (
            <StyledTranscriptTokens
              data-name="transcript-viewer-tokens"
              ref={viewerRef}
            >
              {tokensWithDividers.map(item => {
                if (item.type === 'languageChange') {
                  return (
                    <LanguageChangeDivider
                      key={`lang-change-${item.data.timestamp}`}
                      language={item.data.language}
                      isManual={item.data.isManual}
                    />
                  );
                }

                const token = item.data;
                return isKeywordSetsEnabled ? (
                  <TranscriptTokenWithHighlighting
                    key={token.token_id}
                    token={token}
                    selectedTranscript={selectedTranscript}
                  />
                ) : (
                  <TranscriptToken
                    key={token?.token_id}
                    token={token}
                    selectedTranscript={selectedTranscript}
                  />
                );
              })}
            </StyledTranscriptTokens>
          )}
        </StyledTranscriptViewer>

        {tokens.length !== 0 && newMessagesWhileScrolledUp && !isAtBottom && (
          <Absolute
            left="50%"
            bottom={isTextToVoiceSelected ? '120px' : '16px'}
            style={{
              // shift the indicator to the left by half of its width to center it
              transform: 'translate(-50%, 0)',
            }}
          >
            <NewMessagesIndicator
              onClick={() => {
                if (!viewerRef.current) return;
                viewerRef.current.scrollTop = viewerRef.current.scrollHeight;
                setIsAtBottom(true);
                setNewMessagesWhileScrolledUp(false);
              }}
            />
          </Absolute>
        )}
        {!isReadOnlyView && isTextToVoiceSelected && !isCallEnded && (
          <TextToVoiceContainer>
            <Box
              horizontal
              py="8px"
              px="12px"
              justify="space-between"
              align="center"
              gridColumnGap="8px"
            >
              <Box horizontal align="center" gridColumnGap="8px">
                <TextToVoiceIcon width={16} height={16} color="secondaryText" />
                <Text size="normal" color="primaryText" fontWeight="semibold">
                  Text-to-Voice Translation
                </Text>
                <Popover
                  cssDisplayProperty="inline-block"
                  data-name="pin-tooltip"
                  isOpen={isPinTooltipVisible}
                  popoverID="pin-tooltip"
                  trigger={
                    <StyledHoverableText
                      size="normal"
                      color="primaryText"
                      data-id="pin-text"
                      onClick={handlePinCopy}
                      onMouseEnter={handlePinMouseEnter}
                      onMouseLeave={handlePinMouseLeave}
                    >
                      PIN {pinJobID}
                    </StyledHoverableText>
                  }
                  useCenterXOffset
                  yOffset={58}
                >
                  <PopoverBox ref={pinPopoverRef}>
                    <PopoverText>{isPinCopied ? 'Copied' : 'Copy'}</PopoverText>
                  </PopoverBox>
                </Popover>
              </Box>
              <Box flexDirection="column" alignItems="flex-end">
                <Popover
                  cssDisplayProperty="inline-block"
                  data-name="phone-tooltip"
                  isOpen={isPhoneTooltipVisible}
                  popoverID="phone-tooltip"
                  trigger={
                    <StyledHoverableText
                      size="normal"
                      color="secondaryText"
                      data-id="phone-number-text"
                      onClick={handlePhoneNumberCopy}
                      onMouseEnter={handlePhoneMouseEnter}
                      onMouseLeave={handlePhoneMouseLeave}
                    >
                      Dial {midasConferenceNumber}
                    </StyledHoverableText>
                  }
                  useCenterXOffset
                  yOffset={58}
                >
                  <PopoverBox ref={phonePopoverRef}>
                    <PopoverText>
                      {isPhoneNumberCopied ? 'Copied' : 'Copy'}
                    </PopoverText>
                  </PopoverBox>
                </Popover>
              </Box>
            </Box>
            <Box
              horizontal
              width="100%"
              borderTop="1px solid"
              borderColor="heavyLineOutline"
              padding="12px"
            >
              <TextArea
                placeholder="Type your response and HARMONY will translate it"
                value={textToVoiceInputValue}
                onChange={e => setTextToVoiceInputValue(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    handleSubmit(e);
                  }
                }}
              />
              <Box
                p="16px"
                onClick={handleSubmit}
                color="primaryText"
                cursor="pointer"
              >
                <PaperPlaneIcon width={16} height={16} color="secondaryText" />
              </Box>
            </Box>
          </TextToVoiceContainer>
        )}
      </Container>
      <TextToVoiceModal
        isOpened={isModalVisible}
        onClose={() => setIsModalVisible(false)}
        pinJobID={pinJobID}
        midasConferenceNumber={midasConferenceNumber}
      />
    </>
  );
};

export default TranscriptViewer;
